// Warm-cache verdict of a job: did its cargo build reuse the warm-target
// snapshot (`warm`), or compile units it should not have (`cold N`)? Data:
// internal/api migration 0019 (cold_unit_count / unexpected_cold_count /
// cold_top on the jobs list) and `memory_telemetry.cold_units` on the job
// detail. Plan: internal/compiler/plans/2026-10-14-warm-cache-coverage.md.
//
// classify* mirror internal/api shared/coldUnits.ts (the API stores the counts
// with the same rule; both test files pin the same cases).

import { phaseRows, type MemTelemetry } from "./memTelemetry.ts";

/** The list/summary columns of an admin jobs row (migration 0019). */
export interface CacheFields {
  cold_unit_count?: number | null;
  unexpected_cold_count?: number | null;
  cold_top?: string | null;
}

/** From this many unexpected cold units the chip turns red (partial miss below). */
export const COLD_RED_AT = 10;

export type CacheKind = "warm" | "cold" | "unknown";

export interface CacheVerdict {
  kind: CacheKind;
  /** chip text: "warm" | "cold 7" | "unknown" */
  label: string;
  /** Badge variant */
  tone: "success" | "warning" | "destructive" | "secondary";
  title: string;
}

export const UNKNOWN_TITLE =
  "No cold-unit data: the job's runner predates cold-unit reporting, or the build never reached cargo.";

/** `lib_ts (build-script)` / `hashbrown@0.14.5` / `hashbrown v0.14.5` -> `hashbrown`. */
export function bareUnitName(name: string): string {
  return name
    .replace(/ \(build-script\)$/, "")
    .replace(/@.*$/, "")
    .replace(/ v\d.*$/, "")
    .trim();
}

/** The program's own generated crates and wrapper (expected to compile cold). */
export function isExpectedColdUnit(
  name: string,
  opts: { pgoUse?: boolean } = {},
): boolean {
  const n = bareUnitName(name);
  return (
    n === "pure_test_unit" ||
    /^lib_[A-Za-z0-9_-]+$/.test(n) ||
    /_napi$/.test(n) ||
    (opts.pgoUse === true && FIRST_PARTY_RUNTIME_CRATES.includes(n))
  );
}

/** First-party runtime crates: a `--pgo-use` build recompiles these cold by design
 *  (a `--pgo-generate` build does not: the instrumented variants are prewarmed)
 *  (mirrors api shared/coldUnits.ts FIRST_PARTY_RUNTIME_CRATES); third-party
 *  units must still come from the warm cache. */
export const FIRST_PARTY_RUNTIME_CRATES: readonly string[] = [
  "pure_runtime",
  "pure_runtime_intl",
  "pure_runtime_bundler",
  "pure_runtime_wasm",
  "pure_runtime_crypto",
  "pure_runtime_net",
  "pure_runtime_facade",
  "pure_heap",
  "pure_runtime_sqlite",
  "pure_interp",
];

/** Verdict from the list columns. `pgoByDesign`: the build is an LLVM PGO
 *  build, which compiles everything cold on purpose (plan section 5) — the
 *  verdict is unchanged but the tooltip says so. */
export function cacheVerdict(
  f: CacheFields,
  opts: { pgoByDesign?: boolean } = {},
): CacheVerdict {
  const total = f.cold_unit_count;
  const bad = f.unexpected_cold_count;
  if (total == null || bad == null) {
    return {
      kind: "unknown",
      label: "unknown",
      tone: "secondary",
      title: UNKNOWN_TITLE,
    };
  }
  if (bad === 0) {
    return {
      kind: "warm",
      label: "warm",
      tone: "success",
      title:
        total === 0
          ? "Warm cache hit: cargo compiled nothing."
          : `Warm cache hit: cargo compiled only the program's own crates and wrapper (${total} unit${total === 1 ? "" : "s"}).`,
    };
  }
  const lines = [
    `Cold build: ${bad} of ${total} compiled unit${total === 1 ? "" : "s"} should have come from the warm cache.`,
  ];
  if (f.cold_top) lines.push(`Includes: ${f.cold_top}.`);
  if (opts.pgoByDesign)
    lines.push(
      "LLVM PGO builds recompile the first-party crates (pure_runtime, the program's own crates) cold by design; every third-party unit should still come from the cache.",
    );
  lines.push("Open the job for the full list.");
  return {
    kind: "cold",
    label: `cold ${bad}`,
    tone: bad >= COLD_RED_AT ? "destructive" : "warning",
    title: lines.join("\n"),
  };
}

/** Is this a `--pgo-use` (final, profile-guided) build: first-party crates cold by design? */
export function isPgoUseBuild(f: {
  build_kind?: string | null;
  pgo_profile_path?: string | null;
}): boolean {
  return f.build_kind === "final-pgo" || !!f.pgo_profile_path;
}

/** Is this build an LLVM PGO one (instrumented, training or guided)? */
export function isPgoBuild(f: {
  build_kind?: string | null;
  pgo_generate?: number | null;
  pgo_profile_path?: string | null;
}): boolean {
  return (
    f.build_kind === "llvm-instrumented" ||
    f.build_kind === "llvm-training" ||
    f.build_kind === "final-pgo" ||
    !!f.pgo_generate ||
    !!f.pgo_profile_path
  );
}

/** The chip a job row shows (jobs list, registry attempt builds, sibling
 *  lists): none while the build has not finished (no data yet) and none for a
 *  failed/cancelled job that reported nothing (it may never have reached
 *  cargo); `unknown` for a succeeded job with no data (old runner). */
export function chipVerdict(
  job: CacheFields & Parameters<typeof isPgoBuild>[0] & { status?: string },
): CacheVerdict | null {
  if (job.status === "queued" || job.status === "running") return null;
  const v = cacheVerdict(job, { pgoByDesign: isPgoUseBuild(job) });
  if (v.kind === "unknown" && job.status !== "succeeded") return null;
  return v;
}

// ── detail ───────────────────────────────────────────────────────────────────

export interface ColdUnitRow {
  /** crate name without version / build-script suffix */
  name: string;
  version: string | null;
  buildScript: boolean;
  expected: boolean;
  /** cargo's dirty reason(s) when PURE_RUNNER_BUILD_DIAG was on */
  reasons: string[];
}

export interface CacheDetail {
  verdict: CacheVerdict;
  compiled: number | null;
  fresh: number | null;
  expected: ColdUnitRow[];
  unexpected: ColdUnitRow[];
  /** names beyond the runner's 60-name cap */
  unlisted: number;
  /** wall time spent in cargo (sum of the cargo bands, else the whole
   *  "cargo build" phase), null when the job has no phase data */
  cargoMs: number | null;
  source: "marker" | "compiling-lines" | null;
}

/** `hashbrown@0.14.5` | `hashbrown v0.14.5` | `lib_ts (build-script)` -> parts. */
export function parseUnit(raw: string): {
  name: string;
  version: string | null;
  buildScript: boolean;
} {
  const buildScript = / \(build-script\)$/.test(raw);
  const name = bareUnitName(raw);
  const v = /(?:@| v)(\d[^\s]*)/.exec(raw.replace(/ \(build-script\)$/, ""));
  return { name, version: v ? v[1]! : null, buildScript };
}

/** Wall time cargo took: the per-band phases when the guest reported them,
 *  else the compiler's own "cargo build" phase. */
export function cargoMsOf(t: MemTelemetry | null): number | null {
  if (!t) return null;
  const rows = phaseRows(t);
  const bands = rows.filter(
    (r) =>
      r.kind === "cargo-deps" ||
      r.kind === "cargo-runtime" ||
      r.kind === "cargo-package",
  );
  const pick =
    bands.length > 0 ? bands : rows.filter((r) => r.kind === "cargo");
  if (pick.length === 0) return null;
  return pick.reduce((s, r) => s + r.durationMs, 0);
}

/** One line saying where the job's cargo target dir came from. */
export function seedLine(seed: MemTelemetry["warm_seed"]): string {
  if (!seed) return "Seed provenance: not recorded (runner predates it).";
  if (seed.seeded)
    return `Seeded from warm snapshot ${seed.key ?? "?"}${
      seed.snapshot_created_at
        ? ` (snapshot captured ${seed.snapshot_created_at})`
        : ""
    }; a snapshot older than the current compiler build can still be a stale seed.`;
  return `NOT seeded: the target dir started empty${seed.why ? ` (${seed.why})` : ""}.`;
}

/** Reconciles the count-based verdict with the evidence that units were
 *  actually reused. "warm" is never the default: zero units seen is no data;
 *  a build that was not seeded cannot be warm. */
function honestVerdict(
  v: CacheVerdict,
  cu: NonNullable<MemTelemetry["cold_units"]>,
  seed: MemTelemetry["warm_seed"],
): CacheVerdict {
  const counts =
    cu.fresh != null
      ? `Cargo units: ${cu.compiled} compiled (cold), ${cu.fresh} reused (warm).`
      : `Cargo units: ${cu.compiled} compiled (cold); reused count unknown.`;
  if (cu.compiled === 0 && !(cu.fresh && cu.fresh > 0))
    return {
      kind: "unknown",
      label: "unknown",
      tone: "secondary",
      title: `The log reported no cargo units at all (neither compiled nor reused): no data, not a warm hit.\n${seedLine(seed)}`,
    };
  const sl = seedLine(seed);
  if (v.kind === "warm" && seed && !seed.seeded)
    return {
      kind: "unknown",
      label: "unverified",
      tone: "secondary",
      title: `Only the program's own crates were compiled, but the target dir was not seeded: the data is inconsistent, not a confirmed warm hit.\n${counts}\n${sl}`,
    };
  return { ...v, title: `${v.title}\n${counts}\n${sl}` };
}

export function cacheDetail(
  t: MemTelemetry | null,
  f: CacheFields & Parameters<typeof isPgoBuild>[0] = {},
): CacheDetail {
  const cu = t?.cold_units;
  let verdict = cacheVerdict(
    cu
      ? {
          cold_unit_count: cu.compiled,
          unexpected_cold_count: unexpectedCount(cu, {
            pgoUse: isPgoUseBuild(f),
          }),
          cold_top: null,
        }
      : f,
    { pgoByDesign: isPgoUseBuild(f) },
  );
  if (cu) verdict = honestVerdict(verdict, cu, t?.warm_seed);
  if (!cu) {
    return {
      verdict,
      compiled: f.cold_unit_count ?? null,
      fresh: null,
      expected: [],
      unexpected: [],
      unlisted: 0,
      cargoMs: cargoMsOf(t),
      source: null,
    };
  }
  const reasons = new Map<string, string[]>();
  for (const d of cu.dirty ?? []) {
    const k = bareUnitName(d.unit);
    reasons.set(k, [...(reasons.get(k) ?? []), d.reason]);
  }
  const rows: ColdUnitRow[] = cu.cold.map((raw) => {
    const p = parseUnit(raw);
    return {
      ...p,
      expected: isExpectedColdUnit(raw, { pgoUse: isPgoUseBuild(f) }),
      reasons: reasons.get(p.name) ?? [],
    };
  });
  return {
    verdict,
    compiled: cu.compiled,
    fresh: cu.fresh ?? null,
    expected: rows.filter((r) => r.expected),
    unexpected: rows.filter((r) => !r.expected),
    unlisted: Math.max(0, cu.compiled - cu.cold.length),
    cargoMs: cargoMsOf(t),
    source: cu.source,
  };
}

/** Same rule as the API's summarizeCold (shared/coldUnits.ts). */
export function unexpectedCount(
  cu: {
    compiled: number;
    cold: string[];
  },
  opts: { pgoUse?: boolean } = {},
): number {
  const expected = cu.cold.filter((n) => isExpectedColdUnit(n, opts)).length;
  const listed = cu.cold.length - expected;
  const capped = cu.cold.length >= 60 && cu.compiled > cu.cold.length;
  return capped ? Math.max(listed, cu.compiled - expected) : listed;
}
